import React, { useState, useEffect, useRef, createContext, useContext, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LogOut, ChevronRight, ChevronDown, Pin, PinOff, Smartphone } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { usePermissions } from '@/contexts/PermissionsContext';
import { SIDEBAR_NAVIGATION } from '@/lib/sidebarNav';
import { hrefForSelectedRecord, readSelectedRecord, SELECTED_RECORD_EVENT } from '@/lib/selectedRecord';
import api from '@/lib/axios';
import { cn } from '@/lib/utils';
import TenantSwitcher from './TenantSwitcher';

const COLLAPSE_DELAY_MS = 220;
const AUTO_HIDE_KEY = 'mamv.sidebar.autoHide';
const LEGACY_PIN_KEY = 'mamv.sidebar.pinned';

function loadAutoHide() {
  try {
    const v = localStorage.getItem(AUTO_HIDE_KEY);
    if (v === '1') return true;
    if (v === '0') return false;
    // Migration: früher pinned=1 → dauerhaft sichtbar; pinned=0 → Hover
    const legacy = localStorage.getItem(LEGACY_PIN_KEY);
    if (legacy === '1') return false;
    if (legacy === '0') return true;
  } catch { /* ignore */ }
  return false; // Standard: Menü dauerhaft sichtbar
}

const SidebarUiContext = createContext({
  autoHide: false,
  visible: true,
  expand: () => {},
  collapseSoon: () => {},
  toggleAutoHide: () => {},
});

export function SidebarProvider({ children }) {
  const [autoHide, setAutoHide] = useState(loadAutoHide);
  const [open, setOpen] = useState(() => !loadAutoHide());
  const leaveTimer = useRef(null);

  useEffect(() => () => {
    if (leaveTimer.current) clearTimeout(leaveTimer.current);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(AUTO_HIDE_KEY, autoHide ? '1' : '0');
      localStorage.removeItem(LEGACY_PIN_KEY);
    } catch { /* ignore */ }
    if (!autoHide) {
      if (leaveTimer.current) clearTimeout(leaveTimer.current);
      setOpen(true);
    }
  }, [autoHide]);

  const expand = () => {
    if (leaveTimer.current) {
      clearTimeout(leaveTimer.current);
      leaveTimer.current = null;
    }
    setOpen(true);
  };

  const collapseSoon = () => {
    if (!autoHide) return;
    if (leaveTimer.current) clearTimeout(leaveTimer.current);
    leaveTimer.current = setTimeout(() => setOpen(false), COLLAPSE_DELAY_MS);
  };

  const toggleAutoHide = () => {
    setAutoHide((h) => {
      const next = !h;
      if (next) {
        // Pin → Menü sofort ausblenden
        if (leaveTimer.current) clearTimeout(leaveTimer.current);
        setOpen(false);
      } else {
        setOpen(true);
      }
      return next;
    });
  };

  const visible = !autoHide || open;

  return (
    <SidebarUiContext.Provider value={{ autoHide, visible, expand, collapseSoon, toggleAutoHide }}>
      {children}
    </SidebarUiContext.Provider>
  );
}

function useSidebarUi() {
  return useContext(SidebarUiContext);
}

function BrandMark() {
  const { activeTenant } = useTenant();
  const [logoError, setLogoError] = useState(false);

  const tenantLogoUrl = activeTenant?.logo_url
    ? `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}${activeTenant.logo_url}`
    : null;

  useEffect(() => {
    setLogoError(false);
  }, [activeTenant?.id]);

  if (tenantLogoUrl && !logoError) {
    return (
      <img
        src={tenantLogoUrl}
        alt={activeTenant?.name || 'Mandant'}
        className="max-h-full max-w-full w-auto h-auto object-contain"
        data-testid="tenant-logo"
        onError={() => setLogoError(true)}
      />
    );
  }
  if (activeTenant) {
    return (
      <div
        className="w-full rounded-md border border-brand-border bg-brand-canvas px-3 py-2 flex items-center justify-center"
        data-testid="tenant-logo-placeholder"
      >
        <div className="text-center">
          <p className="text-[10px] uppercase tracking-wide text-brand-red font-semibold leading-none">
            Mandant
          </p>
          <p className="text-sm font-semibold text-brand-anthracite leading-tight mt-0.5 line-clamp-2">
            {activeTenant.name}
          </p>
        </div>
      </div>
    );
  }
  // Desktop-Fallback: Wortmarke — das MA-Mark gehört nur in die Companion (/m).
  return (
    <img
      src="/images/logo.jpg"
      alt="Musik Akademie Meyersick gGmbH"
      className="max-h-full max-w-full w-auto h-auto object-contain"
      data-testid="app-logo"
    />
  );
}

/** Logo-Bereich: weißer Hintergrund (Logos mit weißem Bildrand), gleiche Höhe wie Header */
export function SidebarBrand() {
  const { autoHide, toggleAutoHide } = useSidebarUi();

  return (
    <div
      className="w-64 shrink-0 self-stretch bg-white border-r border-b border-brand-border relative flex items-center justify-center px-3 py-2"
      data-testid="sidebar-brand"
    >
      <button
        type="button"
        onClick={toggleAutoHide}
        className={cn(
          'absolute top-1.5 right-1.5 p-1.5 rounded-md transition-colors z-10',
          autoHide
            ? 'bg-brand-red text-white hover:bg-brand-red/90'
            : 'text-brand-muted hover:bg-brand-canvas hover:text-brand-anthracite',
        )}
        title={autoHide ? 'Menü dauerhaft einblenden' : 'Menü ausblenden (nur bei Hover)'}
        aria-pressed={autoHide}
        data-testid="sidebar-pin-toggle"
      >
        {autoHide ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
      </button>
      <div className="h-full w-full flex items-center justify-center pr-7 min-h-0">
        <BrandMark />
      </div>
    </div>
  );
}

/** Navigation unter dem Logo — dauerhaft oder bei Hover */
export function SidebarNav() {
  const location = useLocation();
  const { logout, user } = useAuth();
  const { activeTenant, isSuperAdmin } = useTenant();
  const { can, loaded: permsLoaded } = usePermissions();
  const { autoHide, visible, expand, collapseSoon } = useSidebarUi();
  const [selectedRecord, setSelectedRecord] = useState(readSelectedRecord);
  const [unread, setUnread] = useState(0);
  const [mailApprovalPending, setMailApprovalPending] = useState(0);
  const navRef = useRef(null);
  const roleNames = new Set((user?.roles || []).map((role) => role.name));
  const isTeacherOnly = (roleNames.has('teacher') || roleNames.has('lehrer'))
    && !isSuperAdmin
    && !['admin', 'secretary', 'sekretariat', 'verwaltung', 'management']
      .some((role) => roleNames.has(role));

  const hiddenMenuIds = new Set(
    (activeTenant?.sidebar_menu?.hidden || []).filter(Boolean),
  );

  useEffect(() => {
    const sync = () => setSelectedRecord(readSelectedRecord());
    sync();
    window.addEventListener(SELECTED_RECORD_EVENT, sync);
    return () => window.removeEventListener(SELECTED_RECORD_EVENT, sync);
  }, [location.pathname]);

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    const fetchUnread = () => {
      api.get('/messages/unread-count')
        .then((r) => { if (!cancelled) setUnread(r.data.unread || 0); })
        .catch(() => {});
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 30000);
    const pathHandler = () => fetchUnread();
    window.addEventListener('focus', pathHandler);
    return () => { cancelled = true; clearInterval(id); window.removeEventListener('focus', pathHandler); };
  }, [user, activeTenant?.id]);

  useEffect(() => {
    if (!user || !isSuperAdmin) return undefined;
    let cancelled = false;
    const fetchMail = () => {
      api.get('/admin/email-queue/summary')
        .then((r) => { if (!cancelled) setMailApprovalPending(r.data.pending || 0); })
        .catch(() => {});
    };
    fetchMail();
    const id = setInterval(fetchMail, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user, isSuperAdmin, activeTenant?.id]);

  const navTree = useMemo(() => {
    const allowLink = (item) => {
      if (item.superAdminOnly && !isSuperAdmin) return false;
      if (item.hideForTeacher && isTeacherOnly) return false;
      if (item.id && !item.superAdminOnly && hiddenMenuIds.has(item.id)) return false;
      if (item.roles && !isSuperAdmin) {
        if (!user?.roles?.some((r) => item.roles.includes(r.name))) return false;
      }
      if (item.anyResource && permsLoaded
          && !item.anyResource.some((r) => can(r, 'read'))) {
        return false;
      }
      if (item.resource && permsLoaded && !can(item.resource, 'read')) {
        return false;
      }
      return true;
    };

    return SIDEBAR_NAVIGATION.map((item) => {
      if (item.type === 'group') {
        if (item.superAdminOnly && !isSuperAdmin) return null;
        const children = (item.children || []).filter(allowLink);
        if (!children.length) return null;
        return { ...item, children };
      }
      return allowLink(item) ? item : null;
    }).filter(Boolean);
  }, [hiddenMenuIds, isSuperAdmin, isTeacherOnly, user, permsLoaded, can]);

  const pathMatches = (href) => {
    if (!href) return false;
    if (href === '/') return location.pathname === '/';
    return location.pathname === href || location.pathname.startsWith(`${href}/`);
  };

  /** Längster Treffer gewinnt — nur ein Eintrag ist aktiv */
  const activeHref = useMemo(() => {
    const links = [];
    for (const item of navTree) {
      if (item.type === 'group') links.push(...(item.children || []));
      else links.push(item);
    }
    const matches = links.filter((l) => pathMatches(l.href));
    if (!matches.length) return null;
    return matches.sort((a, b) => (b.href?.length || 0) - (a.href?.length || 0))[0].href;
  }, [navTree, location.pathname]);

  const pathActive = (href) => href && href === activeHref;

  const groupHasActive = (group) =>
    (group.children || []).some((c) => pathMatches(c.href));

  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const raw = localStorage.getItem('mamv.sidebar.openGroups');
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return {};
  });

  useEffect(() => {
    setOpenGroups((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const item of navTree) {
        if (item.type === 'group' && groupHasActive(item) && !next[item.id]) {
          next[item.id] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [location.pathname, navTree, visible]);

  useEffect(() => {
    try { localStorage.setItem('mamv.sidebar.openGroups', JSON.stringify(openGroups)); } catch { /* ignore */ }
  }, [openGroups]);

  // Aktiven Eintrag in die Mitte scrollen (Navigation, Menü aufklappen, Gruppe öffnen)
  useEffect(() => {
    if (!visible || !activeHref) return undefined;
    let cancelled = false;
    const scroll = () => {
      if (cancelled) return;
      const el = navRef.current?.querySelector('[data-nav-active="true"]');
      if (el) el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
    };
    // Nach Layout (Gruppe aufgeklappt / aside sichtbar)
    const t1 = window.setTimeout(scroll, 40);
    const t2 = window.setTimeout(scroll, 220);
    return () => {
      cancelled = true;
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [visible, activeHref, openGroups, location.pathname]);

  const toggleGroup = (id) => {
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const renderLink = (item, nested = false) => {
    const isActive = pathActive(item.href);
    const showBadge = item.showUnreadBadge && unread > 0;
    const showMailBadge = item.showMailApprovalBadge && mailApprovalPending > 0;
    const Icon = item.icon;
    return (
      <Link
        key={item.id || item.name}
        to={hrefForSelectedRecord(item.href, selectedRecord)}
        data-testid={`nav-${item.name.toLowerCase().replace(/ /g, '-')}`}
        data-nav-active={isActive ? 'true' : undefined}
        aria-current={isActive ? 'page' : undefined}
        className={cn(
          'flex items-center gap-3 rounded-md text-sm font-medium transition-all min-w-0',
          nested ? 'px-3 py-1.5 ml-2' : 'px-3 py-2',
          isActive
            ? 'bg-brand-red text-white'
            : 'text-white/70 hover:bg-white/10 hover:text-white'
        )}
      >
        {Icon && <Icon className={cn('shrink-0', nested ? 'w-4 h-4' : 'w-5 h-5')} strokeWidth={1.5} />}
        <span className="flex-1 min-w-0 truncate" title={item.name}>{item.name}</span>
        {showBadge && (
          <span
            className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-brand-red text-white text-[10px] font-semibold"
            data-testid="sidebar-messages-badge"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
        {showMailBadge && (
          <span
            className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-brand-red text-white text-[10px] font-semibold"
            data-testid="sidebar-mail-approval-badge"
          >
            {mailApprovalPending > 99 ? '99+' : mailApprovalPending}
          </span>
        )}
      </Link>
    );
  };

  return (
    <div
      className={cn(
        'relative shrink-0 h-full transition-[width] duration-200',
        !autoHide || visible ? 'w-64' : 'w-3',
      )}
      data-testid="sidebar-nav-shell"
      onMouseEnter={autoHide ? expand : undefined}
      onFocus={autoHide ? expand : undefined}
    >
      {autoHide && !visible && (
        <div
          className="absolute left-0 top-8 flex items-center justify-center w-3 h-20 rounded-r-md bg-brand-anthracite/80 text-white/70 z-10"
          title="Menü einblenden"
          data-testid="sidebar-hover-rail"
        >
          <ChevronRight className="w-3 h-3" />
        </div>
      )}

      <aside
        className={cn(
          'absolute left-0 top-0 bottom-0 w-64 z-40 flex flex-col',
          'border-r border-white/10 bg-brand-anthracite',
          'transition-transform duration-200 ease-out',
          autoHide ? 'shadow-xl' : 'shadow-none',
          visible ? 'translate-x-0' : '-translate-x-full pointer-events-none',
        )}
        data-testid="sidebar"
        data-open={visible ? 'true' : 'false'}
        data-auto-hide={autoHide ? 'true' : 'false'}
        onMouseEnter={autoHide ? expand : undefined}
        onMouseLeave={autoHide ? collapseSoon : undefined}
        onFocusCapture={autoHide ? expand : undefined}
        onBlurCapture={autoHide ? ((e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) collapseSoon();
        }) : undefined}
      >
        <TenantSwitcher />

        <nav ref={navRef} className="flex-1 p-3 space-y-0.5 overflow-y-auto" data-testid="main-navigation">
          {navTree.map((item) => {
            if (item.type === 'group') {
              const open = openGroups[item.id] === true;
              const GroupIcon = item.icon;
              const groupBadge = (item.children || []).reduce((n, c) => {
                if (c.showUnreadBadge && unread > 0) return n + unread;
                if (c.showMailApprovalBadge && mailApprovalPending > 0) return n + mailApprovalPending;
                return n;
              }, 0);
              return (
                <div key={item.id} className="mb-1" data-testid={`nav-group-${item.id}`}>
                  <button
                    type="button"
                    onClick={() => toggleGroup(item.id)}
                    className={cn(
                      'w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold transition-colors',
                      groupHasActive(item)
                        ? 'text-white bg-white/10'
                        : 'text-white/80 hover:bg-white/10 hover:text-white',
                    )}
                    aria-expanded={open}
                    data-testid={`nav-group-toggle-${item.id}`}
                  >
                    {GroupIcon && <GroupIcon className="w-5 h-5 shrink-0" strokeWidth={1.5} />}
                    <span className="flex-1 min-w-0 truncate text-left">{item.name}</span>
                    {groupBadge > 0 && !open && (
                      <span className="inline-flex items-center justify-center min-w-[18px] h-5 px-1 rounded-full bg-brand-red text-white text-[10px] font-semibold">
                        {groupBadge > 99 ? '99+' : groupBadge}
                      </span>
                    )}
                    <ChevronDown className={cn('w-4 h-4 shrink-0 text-white/40 transition-transform', open && 'rotate-180')} />
                  </button>
                  {open && (
                    <div className="mt-0.5 space-y-0.5 border-l border-white/15 ml-4 pl-1">
                      {item.children.map((child) => renderLink(child, true))}
                    </div>
                  )}
                </div>
              );
            }
            return renderLink(item, false);
          })}
        </nav>

        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-brand-red flex items-center justify-center">
              <span className="text-xs font-semibold text-white">
                {user?.full_name?.charAt(0) || user?.username?.charAt(0) || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate" data-testid="user-name">
                {user?.full_name || user?.username}
              </p>
              <p className="text-xs text-white/50 truncate">
                {user?.roles?.[0]?.name || 'User'}
              </p>
            </div>
          </div>
          <Link
            to="/m"
            data-testid="sidebar-back-to-app"
            className="w-full flex items-center gap-2 px-3 py-2 mb-1 text-sm text-white/70 hover:bg-white/10 hover:text-white rounded-md transition-all"
          >
            <Smartphone className="w-4 h-4" />
            Zur App
          </Link>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:bg-white/10 hover:text-white rounded-md transition-all"
          >
            <LogOut className="w-4 h-4" />
            Abmelden
          </button>
        </div>
      </aside>
    </div>
  );
}

/** @deprecated – Layout nutzt SidebarBrand + SidebarNav */
export default function Sidebar() {
  return null;
}
